import { Component, OnInit, OnDestroy, HostListener, PLATFORM_ID, inject, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService, RouteHelperService } from '../../../../core';
import { filter, Subscription } from 'rxjs';

// Bootstrap se carga globalmente (ver angular.json > scripts) como bootstrap.bundle.min.js.
declare const bootstrap: any;

@Component({
    selector: 'app-navbar',
    standalone: true,
    imports: [CommonModule, RouterLink, TranslateModule],
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit, OnDestroy {
    showNavbar: boolean = true;
    navbarBackground: boolean = false;
    lastScrollTop: number = 0;
    currentRoute: string = '';
    currentLanguage: string = 'es';
    languageDropdownOpen: boolean = false;

    @ViewChild('navMenuEl') navMenuEl?: ElementRef<HTMLElement>;

    private routerSubscription?: Subscription;
    private platformId = inject(PLATFORM_ID);
    private elementRef = inject(ElementRef);
    private router = inject(Router);
    private languageService = inject(LanguageService);
    private translate = inject(TranslateService);
    public routeHelper = inject(RouteHelperService);

    @HostListener('window:scroll', [])
    onWindowScroll() {
        if (!isPlatformBrowser(this.platformId)) return;

        const currentScroll = window.scrollY;

        // Lógica para el navbar
        if (currentScroll > 50) {
            this.navbarBackground = true;
            if (currentScroll > this.lastScrollTop) {
                this.showNavbar = false;
            } else {
                this.showNavbar = true;
            }
        } else {
            this.navbarBackground = false;
            this.showNavbar = true;
        }

        this.lastScrollTop = currentScroll;
    }

    // Cierra el menú móvil al hacer click fuera del navbar
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!isPlatformBrowser(this.platformId)) return;
        if (this.elementRef.nativeElement.contains(event.target)) return;
        this.closeMobileMenu();
    }

    // Cierra el menú móvil colapsado usando la API de Bootstrap.
    closeMobileMenu(): void {
        if (!isPlatformBrowser(this.platformId) || !this.navMenuEl) return;
        const el = this.navMenuEl.nativeElement;
        if (el.classList.contains('show')) {
            bootstrap.Collapse.getOrCreateInstance(el).hide();
        }
    }

    ngOnInit(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        // Establecer la ruta actual inicial
        this.currentRoute = this.router.url;

        // Suscribirse a los cambios de navegación
        this.routerSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: any) => {
            this.currentRoute = event.urlAfterRedirects || event.url;
        });

        // Suscribirse al servicio de idioma
        this.languageService.currentLanguage$.subscribe(lang => {
            this.currentLanguage = lang;
        });
    }

    ngOnDestroy(): void {
        // Limpiar la suscripción para evitar memory leaks
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    // Verifica si la ruta actual coincide con la ruta dada
    isActiveRoute(route: string): boolean {
        const routeWithoutLang = this.currentRoute.replace(/^\/(es|en)/, '');
        const normalizedRoute = routeWithoutLang === '' ? '' : routeWithoutLang;
        const normalizedTarget = route === '' ? '' : route;
        return normalizedRoute === normalizedTarget || normalizedRoute === normalizedTarget + '/';
    }

    toggleLanguageDropdown() {
        this.languageDropdownOpen = !this.languageDropdownOpen;
    }

    changeLanguage(lang: string) {
        this.languageService.setLanguage(lang);
        this.languageDropdownOpen = false;
    }

    getCurrentLanguageFlag(): string {
        return this.currentLanguage === 'es' ? '🇪🇸' : '🇺🇸';
    }

    getCurrentLanguageText(): string {
        return this.currentLanguage === 'es' ? 'ES' : 'EN';
    }
}
